import { buildMakeUseAxios } from "@api";
import type { IApply } from "@bbj-types/apply";
import type { ICandidate } from "@bbj-types/candidate";
import type { IConversation, ISms } from "@bbj-types/message";
import type { IUser } from "@bbj-types/user";
import type { ObjectId, Override } from "@bbj-types/utils";
import type { SmsSentStatus, SmsType } from "@root/utils/enums/communication";
import { useCallback } from "react";

const messageCreditAxios = buildMakeUseAxios(`${import.meta.env.VITE_COMMUNICATION_SERVICE_URL}/smscredit/`);

const axiosMessage = buildMakeUseAxios(`${import.meta.env.VITE_COMMUNICATION_SERVICE_URL}`);

export const useGetMessageCredit = () => {
  const [state, execute, cancel] = messageCreditAxios(
    {
      url: "/",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (organisationId) => {
      return execute({
        url: `/${organisationId}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface ISendMessageBody {
  candidate: string;
  message: string;
  phone: string;
  type: SmsType.SENT;
  sentStatus: SmsSentStatus.PENDING;
}

export const useSendMessage = () => {
  const [state, execute, cancel] = axiosMessage<ISms, ISendMessageBody, unknown>(
    {
      url: "/sms/send",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: ISendMessageBody) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useSendMessageBatch = () => {
  const [state, execute, cancel] = axiosMessage(
    {
      url: "/sms/send/batch",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data) => {
      return execute({
        url: "/sms/send/batch",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IGetMessagesSms {
  _id: ObjectId;
  apply?: Override<Pick<IApply, "_id" | "offer">, { offer: Pick<IApply["offer"], "name"> }>;
  candidate?: Pick<ICandidate, "_id" | "firstName" | "lastName">;
  createdBy?: Pick<IUser, "_id" | "firstName" | "lastName">;
  createdAt: Date;
  updatedAt: Date;
  handled: boolean;
  kind?: string;
  message: string;
  organisation: ObjectId;
  phone: string;
  providerId?: string;
  type: SmsType;
  errorCode?: string;
  sentStatus?: SmsSentStatus;
  scheduledDate?: Date;
}

export interface IGetMessages {
  data: IGetMessagesSms[];
  limit: number;
  skip: number;
  total: number;
  count: number;
}

export interface IGetMessagesParams {
  candidate: string;
  limit?: number;
  sort?: string;
  skip?: number;
}

export const useGetMessages = () => {
  const [state, execute, cancel] = axiosMessage<IGetMessages, undefined, unknown>(
    {
      url: "/sms",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    ({ candidate, limit, sort, skip }: IGetMessagesParams) => {
      const buildQuerryParams = () => {
        let result = `candidate=${candidate}`;
        result = limit ? result.concat(`&limit=${limit}`) : result;
        result = sort ? result.concat(`&sort=${sort}`) : result;
        result = skip ? result.concat(`&skip=${skip}`) : result;
        return result;
      };
      return execute({
        url: `/sms?${buildQuerryParams()}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetMessageStats = () => {
  const [state, execute, cancel] = axiosMessage(
    {
      url: "/sms/stats",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (userId) => {
      return execute({
        url: `/sms/stats?authors=${userId}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IConversationQuery {
  sentHandled?: boolean;
  sentNotHandled?: boolean;
  receivedHandled?: boolean;
  receivedNotHandled?: boolean;
  authors?: ObjectId[];
}

export const useGetConversations = () => {
  const [state, execute, cancel] = axiosMessage<IConversation[]>(
    {
      url: "/v2/sms/conversations",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (query: IConversationQuery) => {
      return execute({
        params: query,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePatchSms = () => {
  const [state, execute, cancel] = axiosMessage<undefined>(
    {
      url: "/v2/sms/{id}",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, data: Partial<ISms>) => {
      return execute({
        url: `/v2/sms/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
export type TBulkPatchSmsCandidateBody = Pick<ISms, "handled">;

export const useBulkPatchCandidateSms = () => {
  const [state, execute, cancel] = axiosMessage<undefined, TBulkPatchSmsCandidateBody, unknown>(
    {
      url: "/v2/sms/received/candidate/{id}",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: string, data: TBulkPatchSmsCandidateBody) => {
      return execute({
        url: `/v2/sms/received/candidate/${candidateId}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useResendSms = () => {
  const [state, execute, cancel] = axiosMessage<undefined>(
    {
      url: "/v2/sms/resend",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string) => {
      return execute({
        url: `/v2/sms/resend/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
